import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { cloneDeep, isArray, keyBy } from 'lodash';
import { Apollo, gql } from 'apollo-angular';
import { QueryOptionsAlone } from 'apollo-angular/types';
import { GetAllInstitutionsGQL, GetCreditsLogGQL } from '../generated/graphql';

export type DataTypeUpdatable = 'yolo';

export type DataType =
  | 'institutions'
  | 'users'
  | 'answers'
  | 'results'
  | 'credits_log'
  | 'registration_billings'
  | 'profiles';

const OPERATIONS: {
  [dataType: string]: {
    get?: any;
    watch?: any;
    list?: any;
    getByPk?: any;
  };
} = {
  institutions: {
    get: 'getAllInstitutionsGQL'
  },
  credits_log: {
    get: 'getCreditsLogGQL'
  }
};

@Injectable({
  providedIn: 'root'
})
export class DatasService {
  private caches: {
    [dataType: string]: Observable<any>;
  } = {};

  private cachesByKey: {
    [dataType: string]: Observable<{
      [keyuid: string]: any;
    }>;
  } = {};

  private cachesSubscription: {
    [dataType: string]: Observable<any>;
  } = {};

  private cachesSubscriptionList: {
    [dataType: string]: Observable<any>;
  } = {};

  constructor(
    private apollo: Apollo,
    private getAllInstitutionsGQL: GetAllInstitutionsGQL,
    private getCreditsLogGQL: GetCreditsLogGQL
  ) {}

  getDatas$<T>(
    type: DataType,
    options: QueryOptionsAlone<any, any> = {}
  ): Observable<T[]> {
    console.log('options', options);
    const graphQlOperation = OPERATIONS[type].get;
    if (!this.caches[type]) {
      this.caches[type] = this[graphQlOperation].fetch({}, options).pipe(
        shareReplay(1),
        map((res: any) => res.data[type]),
        // remove cache
        tap(res => {
          delete this.caches[type];
          delete this.cachesByKey[type];
        })
      );
    }

    return this.caches[type];
  }

  getDataByKey$<T>(type: DataType): Observable<{ [keyUid: string]: T }> {
    if (!this.cachesByKey[type]) {
      this.cachesByKey[type] = this.getDatas$<T>(type).pipe(
        shareReplay(1),
        map(datas => keyBy(datas, 'uid'))
      );
    }

    return this.cachesByKey[type];
  }

  getOneData$<T>(type: DataType, uid: string): Observable<T> {
    return this.getDataByKey$<T>(type).pipe(map(res => res[uid]));
  }

  getDataByPk$<T>(type: DataType, uid: string): Observable<T> {
    const graphQlOperation = OPERATIONS[type]?.getByPk;
    if (graphQlOperation) {
      return this[graphQlOperation]
        .fetch({ uid: uid }, { fetchPolicy: 'no-cache' })
        .pipe(map((res: any) => res.data[`${type}_by_pk`]));
    }
    return this.getDataByKey$<T>(type).pipe(map(res => res[uid]));
  }

  watchDatasList$<T>(type: DataType): Observable<T[]> {
    const graphQlOperation = OPERATIONS[type].list;
    console.log(OPERATIONS[type].list);
    if (!graphQlOperation) {
      return this.watchDatas$<T>(type);
    }
    if (!this.cachesSubscriptionList[type]) {
      console.log(type, this[graphQlOperation]);
      const result$ = this[graphQlOperation].subscribe().pipe(
        tap((res: any) => console.log(res.data)),
        map((res: any) => res.data[type]),
        shareReplay(1)
      );

      // lazy subscription to avoid avoid multiple request and be up to date
      this.cachesSubscriptionList[type] = result$;
      result$.subscribe();
    }

    return this.cachesSubscriptionList[type];
  }

  watchDatas$<T>(type: DataType): Observable<T[]> {
    if (!this.cachesSubscription[type]) {
      console.log(type, !!this.cachesSubscription[type]);
      const graphQlOperation = OPERATIONS[type].watch;
      const result$ = this[graphQlOperation].subscribe().pipe(
        map((res: any) => res.data[type]),
        shareReplay(1)
      );

      // lazy subscription to avoid avoid multiple request and be up to date
      this.cachesSubscription[type] = result$;
      result$.subscribe();
    }

    return this.cachesSubscription[type];
  }

  watchDataByKey$<T>(type: DataType): Observable<{ [keyUid: string]: T }> {
    return this.watchDatas$<T>(type).pipe(map(datas => keyBy(datas, 'uid')));
  }

  watchOneData$<T>(type: DataType, uid: string): Observable<T> {
    return this.watchDataByKey$<T>(type).pipe(map(res => cloneDeep(res[uid])));
  }

  watchOneDataInList$<T>(type: DataType, uid: string): Observable<T> {
    return this.watchDatasList$<T>(type).pipe(
      map(res => keyBy(res, 'uid')),
      map(datas => {
        console.log(datas);
        return cloneDeep(datas[uid]);
      })
    );
  }

  insertData$<T>(
    type: DataType | DataTypeUpdatable,
    role: string,
    object: T | T[],
    pkField: 'uid' | 'id' = 'uid'
  ): Observable<any> {
    let objects: T[];
    if (isArray(object)) {
      objects = object;
    } else {
      objects = [object];
    }

    return this.apollo
      .mutate({
        mutation: gql`mutation insert_${type}($objects: [${type}_insert_input!]!) {
        insert_${type}(objects: $objects) {
          affected_rows
          returning {
            ${pkField}
          }
        }
      }`,
        variables: {
          objects
        },
        context: {
          headers: {
            role
          }
        }
      })
      .pipe(
        map(data => {
          return data.data[`insert_${type}`];
        }),
        tap(res => {
          delete this.caches[type];
          delete this.cachesByKey[type];
        })
      );
  }

  safeDeleteData$<T>(
    type: DataType | DataTypeUpdatable,
    pk: string
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: gql`mutation SafeDelete_${type}ByPk($pk: String!) {
        update_${type}_by_pk(pk_columns: {uid: $pk},  _set:{deletedAt:"${new Date().toISOString()}"}) {
          uid
        }
      }
      `,
      variables: {
        pk
      }
    });
  }

  deleteData$<T>(
    type: DataType | DataTypeUpdatable,
    pk: string
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: gql`mutation Delete_${type}ByPk($pk: String!) {
        delete_${type}_by_pk(uid: $pk) {
          uid
        }
      }
      `,
      variables: {
        pk
      }
    });
  }

  // WE need pk field becuase user has id annd not an uidd
  updateData$<T>(
    type: DataType | DataTypeUpdatable,
    role: string,
    pk: string | number,
    partialObject: Partial<T>,
    pkField: 'uid' | 'id' = 'uid'
  ): Observable<any> {
    const copy = cloneDeep(partialObject);
    if ((<any>copy).__typename) {
      delete (<any>copy).__typename;
    }

    return this.apollo
      .mutate({
        mutation: gql`mutation Update_${type}ByPk($pk: Int!, $partialObject: ${type}_set_input!) {
        update_${type}_by_pk(pk_columns: {${pkField}: $pk}, _set: $partialObject) {
          ${pkField}
        }
      }
      `,
        variables: {
          pk,
          partialObject: copy
        },
        context: {
          headers: {
            role
          }
        }
      })
      .pipe(
        tap(res => {
          delete this.caches[type];
          delete this.cachesByKey[type];
        }),
        catchError(e => {
          console.log(e);
          const errors = e.graphQLErrors.map(error => {
            return error.message;
          });
          console.log(errors);
          return of(e);
        })
      );
  }

  // TODO delete
  queryOneData$<T>(
    type: DataType,
    query: string,
    keys: Partial<keyof T>[]
  ): Observable<Partial<T>> {
    return this.apollo
      .query({
        query: gql`query Get_${type} {
       ${type}(where:${query}) {
          ${keys.toString().replace(',', '\n')}
        }
      }
      `
      })
      .pipe(
        map(data => {
          return data.data[`${type}`][0];
        })
      );
  }
}
